import React, { useMemo, useState } from "react";

import { Button, Checkbox, Row, TextInput } from "@narmi/design_system";

import styles from "./MultiSelectFilterPanel.module.scss";

export interface MultiSelectFilterPanelOption {
  value: string;
  label: string;
  group?: string;
}

export interface MultiSelectFilterPanelProps {
  options: MultiSelectFilterPanelOption[];
  selectedValues: string[];
  setSelectedValues: (values: string[]) => void;
  close: () => void;
  remove: () => void;
  onApply: () => void;
  searchLabel: string;
  emptyLabel: string;
  applyLabel: string;
  removeLabel: string;
  testIdPrefix: string;
  /** Custom search text extractor. Defaults to `option.label`. */
  getSearchText?: (option: MultiSelectFilterPanelOption) => string;
  /**
   * Renders each group header as a checkbox that selects/deselects every
   * option in that group, showing indeterminate when only some are checked.
   * Off by default so existing plain-text-header consumers are unaffected.
   */
  groupSelectable?: boolean;
}

const MultiSelectFilterPanel = ({
  options,
  selectedValues,
  setSelectedValues,
  close,
  remove,
  onApply,
  searchLabel,
  emptyLabel,
  applyLabel,
  removeLabel,
  testIdPrefix,
  getSearchText,
  groupSelectable = false,
}: MultiSelectFilterPanelProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  // Popover unmounts the panel on close, so this reseeds on every open.
  const [draft, setDraft] = useState<Set<string>>(
    () => new Set(selectedValues)
  );

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.trim().toLowerCase();
    return options.filter((option) => {
      const text = getSearchText ? getSearchText(option) : option.label;
      return text.toLowerCase().includes(query);
    });
  }, [options, searchQuery, getSearchText]);

  const groupValues = useMemo(() => {
    const map = new Map<string, string[]>();
    options.forEach((option) => {
      if (!option.group) return;
      const values = map.get(option.group) ?? [];
      values.push(option.value);
      map.set(option.group, values);
    });
    return map;
  }, [options]);

  const getGroupCheckboxState = (group: string) => {
    const values = groupValues.get(group) ?? [];
    const checkedCount = values.filter((value) => draft.has(value)).length;
    return {
      checked: checkedCount > 0 && checkedCount === values.length,
      indeterminate: checkedCount > 0 && checkedCount < values.length,
    };
  };

  const handleToggle = (value: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const handleGroupToggle = (group: string, nextChecked: boolean) => {
    const values = groupValues.get(group) ?? [];
    setDraft((prev) => {
      const next = new Set(prev);
      values.forEach((value) => {
        if (nextChecked) {
          next.add(value);
        } else {
          next.delete(value);
        }
      });
      return next;
    });
  };

  const renderGroupHeader = (
    option: MultiSelectFilterPanelOption,
    index: number
  ) => {
    const { group } = option;
    if (!group) return null;
    const isFirstInGroup =
      index === 0 || filteredOptions[index - 1].group !== group;
    if (!isFirstInGroup) return null;

    if (!groupSelectable) {
      return (
        <span className={styles["multi-select-filter-panel-group-header"]}>
          {group}
        </span>
      );
    }

    const { checked, indeterminate } = getGroupCheckboxState(group);
    const groupTestId = group.toLowerCase().replace(/\s+/g, "-");
    return (
      <div className={styles["multi-select-filter-panel-group-checkbox"]}>
        <Checkbox
          label={group}
          kind="condensed"
          checked={checked}
          indeterminate={indeterminate}
          onChange={() => handleGroupToggle(group, !checked)}
          testId={`${testIdPrefix}-group-${groupTestId}`}
        />
      </div>
    );
  };

  const handleApply = () => {
    setSelectedValues([...draft]);
    onApply();
    close();
  };

  const handleRemove = () => {
    setDraft(new Set());
    setSelectedValues([]);
    onApply();
    remove();
  };

  return (
    <div className={styles["multi-select-filter-panel"]}>
      <div className={styles["multi-select-filter-panel-search"]}>
        <TextInput
          label={searchLabel}
          value={searchQuery}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setSearchQuery(event.target.value)
          }
          testId={`${testIdPrefix}-search`}
        />
      </div>
      <div className={styles["multi-select-filter-panel-list"]}>
        {filteredOptions.length === 0 ? (
          <div className={styles["multi-select-filter-panel-empty"]}>
            {emptyLabel}
          </div>
        ) : (
          filteredOptions.map((option, index) => (
            <React.Fragment key={`${option.value}-${index}`}>
              {renderGroupHeader(option, index)}
              <div
                className={
                  groupSelectable && option.group
                    ? styles["multi-select-filter-panel-nested-checkbox"]
                    : undefined
                }
              >
                <Checkbox
                  label={option.label}
                  kind="condensed"
                  checked={draft.has(option.value)}
                  onChange={() => handleToggle(option.value)}
                />
              </div>
            </React.Fragment>
          ))
        )}
      </div>
      <div className={styles["multi-select-filter-panel-divider"]} />
      <div className="padding--x--s padding--y--xs">
        <Row justifyContent="space-between" alignItems="center">
          <Button
            kind="plain"
            size="s"
            label={removeLabel}
            onClick={handleRemove}
            type="button"
            testId={`${testIdPrefix}-remove`}
          />
          <Button
            kind="primary"
            size="s"
            label={applyLabel}
            onClick={handleApply}
            type="button"
            testId={`${testIdPrefix}-apply`}
          />
        </Row>
      </div>
    </div>
  );
};

export default MultiSelectFilterPanel;
